import { makeAutoObservable } from "mobx";

import { CursorModel } from "@/models/CursorModel";
import { SelectionModel } from "@/models/SelectionModel";

import type { InlineNodeModel } from "./nodes/InlineNodeModel";
import type { DocumentModel } from "@/models/DocumentModel";

export type CursorContext = {
  selection: SelectionModel;
  cursor: CursorModel;
  boundaryInlineNodeIds?: string[];
};

export class CursorMapModel {
  doc: DocumentModel;

  cursors: Map<string, CursorContext> = new Map();

  constructor(doc: DocumentModel, contextId?: string) {
    this.doc = doc;

    if (contextId) {
      this.add(contextId);
    }

    makeAutoObservable(this);
  }

  cursor(contextId: string): CursorModel {
    const context = this.cursors.get(contextId);

    if (context) {
      return context.cursor;
    }

    const { cursor } = this.add(contextId);
    return cursor;
  }

  selection(contextId: string): SelectionModel {
    const context = this.cursors.get(contextId);

    if (context) {
      return context.selection;
    }

    const { selection } = this.add(contextId);
    return selection;
  }

  add(contextId: string): CursorContext {
    if (!this.cursors.has(contextId)) {
      const cursor = new CursorModel(this.doc);
      const selection = new SelectionModel(this.doc);

      this.cursors.set(contextId, {
        selection,
        cursor,
      });
    }

    return this.cursors.get(contextId)!;
  }

  setBoundary(contextId: string, boundary: InlineNodeModel | InlineNodeModel[] | undefined) {
    const context = this.cursors.get(contextId);

    if (context) {
      const ids = boundary
        ? (Array.isArray(boundary) ? boundary : [boundary]).map((n) => n.id)
        : undefined;

      context.boundaryInlineNodeIds = ids;
    }
  }

  remove(contextId: string) {
    const context = this.cursors.get(contextId);

    if (context) {
      context.selection.dispose();
      context.cursor.dispose();
      this.cursors.delete(contextId);
    }
  }

  dispose() {
    this.cursors.forEach(({ cursor, selection }) => {
      selection.dispose();
      cursor.dispose();
    });
    this.cursors.clear();
  }
}
